// EquipmentDetailDialog.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Grid,
  VStack,
  HStack,
  Heading,
  Dialog,
  Button,
  ButtonGroup,
  Badge,
  type BoxProps,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ReferenceLine,
} from "recharts";

// 커스텀 카드 컴포넌트
const CustomCard = ({
  children,
  ...props
}: BoxProps & { children?: React.ReactNode }) => (
  <Box
    bg="white"
    border="1px"
    borderColor="gray.200"
    borderRadius="md"
    p={4}
    shadow="md"
    {...props}
  >
    {children}
  </Box>
);

// Equipment 타입 정의
type Equipment = {
  name: string;
  status: "online" | "offline" | "warning" | "normal";
  spindle?: number;
};

// Historical Data 타입 정의
type HistoricalDataPoint = {
  time: string;
  timestamp: number;
  temperature: number;
  vibration: number;
  efficiency: number;
  spindle: number;
  load: number;
  feed: number;
};

// 3축 진동 데이터 타입 정의
type Vibration3AxisDataPoint = {
  time: string;
  vibrationX: number;
  vibrationY: number;
  vibrationZ: number;
  overall: number;
};

// 주파수 스펙트럼 데이터 타입 정의
type SpectrumDataPoint = {
  frequency: number;
  amplitude: number;
  phase: number;
};

type ResonantFreqs = {
  BPFO: number;
  BPFI: number;
  BSF: number;
  FTF: number;
};

// AI 예측 데이터 타입 정의
type AIPredictionPoint = {
  time: string;
  timestamp: number;
  predictedTemperature: number;
  predictedVibration: number;
  predictedEfficiency: number;
  predictedSpindle: number;
  failureProbability: number;
  confidenceLevel: number;
  riskFactors: {
    temperature: "high" | "medium" | "low";
    vibration: "high" | "medium" | "low";
    trend?: "deteriorating" | "stable";
    overall?: "critical" | "high" | "medium" | "low" | "normal" | "warning";
  };
};

// 진동 센서 타입 정의 (확장됨)
const sensorTypes = [
  {
    key: "temperature",
    label: "🌡️ 온도",
    unit: "°C",
    color: "#3182ce",
    description: "스핀들/베어링 온도",
  },
  {
    key: "vibration_3axis",
    label: "📳 3축 진동",
    unit: "mm/s",
    color: "#38a169",
    description: "3축 진동 분석 (X, Y, Z)",
  },
  {
    key: "vibration_frequency",
    label: "🌊 주파수 스펙트럼",
    unit: "Hz",
    color: "#805ad5",
    description: "진동 주파수 도메인 분석",
  },
  {
    key: "efficiency",
    label: "⚡ 효율",
    unit: "%",
    color: "#dd6b20",
    description: "가동 효율성",
  },
  {
    key: "spindle",
    label: "🔄 스핀들",
    unit: " RPM",
    color: "#e53e3e",
    description: "스핀들 회전속도",
  },
  {
    key: "ai_prediction",
    label: "🤖 AI 예측",
    unit: "",
    color: "#8b5cf6",
    description: "AI 기반 예지보전",
  },
];

// 3축 진동 데이터 생성 함수
const generate3AxisVibrationData = (equipment: Equipment) => {
  const hours = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, "0") + ":00";
    const isWorkingHours = i >= 8 && i <= 18;

    if (equipment.status === "offline") {
      return {
        time: hour,
        vibrationX: 0,
        vibrationY: 0,
        vibrationZ: 0,
        overall: 0,
      };
    }

    // 장비별 진동 특성
    let baseVibX: number, baseVibY: number, baseVibZ: number;

    switch (equipment.name.split(" ")[1]) {
      case "밀링머신":
        baseVibX = isWorkingHours
          ? 1.2 + Math.random() * 0.8
          : 0.2 + Math.random() * 0.2;
        baseVibY = isWorkingHours
          ? 1.0 + Math.random() * 0.6
          : 0.1 + Math.random() * 0.2;
        baseVibZ = isWorkingHours
          ? 0.8 + Math.random() * 0.4
          : 0.1 + Math.random() * 0.15;
        break;
      case "선반":
        baseVibX = isWorkingHours
          ? 0.8 + Math.random() * 0.5
          : 0.1 + Math.random() * 0.1;
        baseVibY = isWorkingHours
          ? 1.5 + Math.random() * 0.7
          : 0.2 + Math.random() * 0.2;
        baseVibZ = isWorkingHours
          ? 0.6 + Math.random() * 0.3
          : 0.1 + Math.random() * 0.1;
        break;
      case "그라인더":
        baseVibX = isWorkingHours
          ? 2.5 + Math.random() * 1.5
          : 0.3 + Math.random() * 0.3;
        baseVibY = isWorkingHours
          ? 2.8 + Math.random() * 1.8
          : 0.4 + Math.random() * 0.4;
        baseVibZ = isWorkingHours
          ? 3.2 + Math.random() * 2.0
          : 0.5 + Math.random() * 0.5;
        break;
      default:
        baseVibX = isWorkingHours
          ? 1.5 + Math.random() * 1.0
          : 0.2 + Math.random() * 0.2;
        baseVibY = isWorkingHours
          ? 1.3 + Math.random() * 0.8
          : 0.15 + Math.random() * 0.15;
        baseVibZ = isWorkingHours
          ? 1.0 + Math.random() * 0.6
          : 0.1 + Math.random() * 0.1;
    }

    // 경고 상태일 때 진동 증가
    if (equipment.status === "warning") {
      baseVibX += 1.0 + Math.random() * 0.5;
      baseVibY += 0.8 + Math.random() * 0.4;
      baseVibZ += 0.6 + Math.random() * 0.3;
    }

    const vibX = Math.round(Math.max(0, Math.min(8, baseVibX)) * 100) / 100;
    const vibY = Math.round(Math.max(0, Math.min(8, baseVibY)) * 100) / 100;
    const vibZ = Math.round(Math.max(0, Math.min(8, baseVibZ)) * 100) / 100;
    const overall =
      Math.round(Math.sqrt(vibX * vibX + vibY * vibY + vibZ * vibZ) * 100) /
      100;

    return {
      time: hour,
      vibrationX: vibX,
      vibrationY: vibY,
      vibrationZ: vibZ,
      overall: overall,
    };
  });
  return hours;
};

const generateInitialData = (equipment: Equipment) => {
  // Add equipment parameter
  const now = new Date();
  const hours = Array.from({ length: 24 }, (_, i) => {
    const pastTime = new Date(now.getTime() - (23 - i) * 60 * 60 * 1000);
    const hour = pastTime.getHours().toString().padStart(2, "0") + ":00";

    // 과거 시간대 설정
    const isWorkingHours =
      pastTime.getHours() >= 8 && pastTime.getHours() <= 18;
    const isBreakTime =
      pastTime.getHours() === 12 || pastTime.getHours() === 15;

    if (equipment.status === "offline") {
      return {
        time: hour,
        timestamp: pastTime.getTime(),
        temperature: Math.round((Math.random() * 5 + 20) * 100) / 100,
        vibration: 0,
        efficiency: 0,
        spindle: 0,
        load: 0,
        feed: 0,
      };
    }

    const values = generateEquipmentBaseValues(
      equipment,
      pastTime.getHours(),
      isWorkingHours,
      isBreakTime
    );

    return {
      time: hour,
      timestamp: pastTime.getTime(),
      ...values,
    };
  });
  return hours;
};

const generateEquipmentBaseValues = (
  equipment: Equipment,
  hour: number,
  isWorkingHours: boolean,
  isBreakTime: boolean
) => {
  // Equipment-specific base values
  const equipmentType = equipment.name.split(" ")[1];
  let baseValues = {
    temperature: { min: 25, max: 45, workingMin: 45, workingMax: 65 },
    vibration: { min: 0.1, max: 0.3, workingMin: 1.0, workingMax: 3.0 },
    efficiency: { min: 0, max: 0, workingMin: 70, workingMax: 90 },
    spindle: { min: 0, max: 0, workingMin: 12000, workingMax: 18000 },
    load: { min: 0, max: 0, workingMin: 50, workingMax: 80 },
    feed: { min: 0, max: 0, workingMin: 800, workingMax: 1500 },
  };

  // Adjust base values based on equipment type
  switch (equipmentType) {
    case "그라인더":
      baseValues.temperature.workingMax = 75;
      baseValues.vibration.workingMax = 4.5;
      baseValues.spindle.workingMin = 20000;
      baseValues.spindle.workingMax = 25000;
      baseValues.feed.workingMin = 500;
      baseValues.feed.workingMax = 1000;
      break;
    case "밀링머신":
      baseValues.temperature.workingMax = 70;
      baseValues.vibration.workingMax = 3.5;
      baseValues.spindle.workingMin = 15000;
      baseValues.spindle.workingMax = 20000;
      baseValues.feed.workingMin = 1000;
      baseValues.feed.workingMax = 2000;
      break;
    case "선반":
      baseValues.temperature.workingMax = 65;
      baseValues.vibration.workingMax = 2.5;
      baseValues.spindle.workingMin = 10000;
      baseValues.spindle.workingMax = 15000;
      baseValues.feed.workingMin = 1200;
      baseValues.feed.workingMax = 1800;
      break;
    default:
      // Use default values
      break;
  }

  // Status-based multipliers
  const statusMultipliers = {
    temperature: equipment.status === "warning" ? 1.2 : 1.0,
    vibration: equipment.status === "warning" ? 1.5 : 1.0,
    efficiency: equipment.status === "warning" ? 0.8 : 1.0,
    spindle: equipment.status === "warning" ? 1.1 : 1.0,
    load: equipment.status === "warning" ? 1.3 : 1.0,
    feed: equipment.status === "warning" ? 0.9 : 1.0,
  };

  // Time-based variations
  const timeVariations = {
    temperature: Math.sin((hour * Math.PI) / 12) * 3, // Temperature varies throughout the day
    vibration: Math.sin((hour * Math.PI) / 8) * 0.2, // Vibration has different pattern
    efficiency: isBreakTime ? -10 : 0, // Lower efficiency during breaks
    spindle: Math.sin((hour * Math.PI) / 10) * 500, // Spindle speed variations
    load: Math.sin((hour * Math.PI) / 6) * 5, // Load variations
    feed: Math.sin((hour * Math.PI) / 8) * 100, // Feed rate variations
  };

  // Generate values
  const generateValue = (param: keyof typeof baseValues) => {
    const base = baseValues[param];
    const multiplier = statusMultipliers[param];
    const variation = timeVariations[param];

    let value: number;
    if (isWorkingHours) {
      const range = base.workingMax - base.workingMin;
      value = base.workingMin + Math.random() * range + variation;
    } else {
      const range = base.max - base.min;
      value = base.min + Math.random() * range + variation * 0.3;
    }

    return Math.max(0, value * multiplier);
  };

  return {
    temperature: Math.round(generateValue("temperature") * 100) / 100,
    vibration: Math.round(generateValue("vibration") * 100) / 100,
    efficiency:
      Math.round(
        Math.max(0, Math.min(100, generateValue("efficiency"))) * 100
      ) / 100,
    spindle: Math.round(generateValue("spindle")),
    load:
      Math.round(Math.max(0, Math.min(100, generateValue("load"))) * 100) / 100,
    feed: Math.round(generateValue("feed")),
  };
};

const generateAIPredictions = (
  equipment: Equipment,
  historicalData: HistoricalDataPoint[]
): AIPredictionPoint[] => {
  const predictions: AIPredictionPoint[] = [];
  const now = new Date();

  // Get the last known values from historical data
  const lastData = historicalData[historicalData.length - 1];
  const recentTrend = historicalData.slice(-6); // Last 6 hours for trend analysis

  // Calculate trends
  const tempTrend =
    recentTrend.length > 1
      ? (recentTrend[recentTrend.length - 1].temperature -
          recentTrend[0].temperature) /
        recentTrend.length
      : 0;
  const vibTrend =
    recentTrend.length > 1
      ? (recentTrend[recentTrend.length - 1].vibration -
          recentTrend[0].vibration) /
        recentTrend.length
      : 0;

  // Base risk factors based on equipment type and current status
  let baseRisk = 0;
  switch (equipment.status) {
    case "warning":
      baseRisk = 45 + Math.random() * 25; // 45-70%
      break;
    case "normal":
      baseRisk = 10 + Math.random() * 20; // 10-30%
      break;
    case "offline":
      baseRisk = 5 + Math.random() * 10; // 5-15%
      break;
    default:
      baseRisk = 15 + Math.random() * 15; // 15-30%
  }

  // Equipment-specific risk adjustments
  const equipmentType = equipment.name.split(" ")[1];
  let equipmentRiskMultiplier = 1.0;

  switch (equipmentType) {
    case "그라인더":
      equipmentRiskMultiplier = 1.3; // Higher risk due to high-speed operation
      break;
    case "밀링머신":
      equipmentRiskMultiplier = 1.1; // Moderate risk
      break;
    case "선반":
      equipmentRiskMultiplier = 1.0; // Standard risk
      break;
    default:
      equipmentRiskMultiplier = 1.0;
  }

  // Generate predictions for next 12 hours
  for (let i = 1; i <= 12; i++) {
    const futureTime = new Date(now.getTime() + i * 60 * 60 * 1000);
    const hour = futureTime.getHours().toString().padStart(2, "0") + ":00";
    const isWorkingHours =
      futureTime.getHours() >= 8 && futureTime.getHours() <= 18;

    // Temperature prediction with trend
    let predictedTemp = lastData.temperature + tempTrend * i;

    // Add some realistic variation
    if (isWorkingHours) {
      predictedTemp += Math.sin(i * 0.5) * 3 + Math.random() * 4 - 2;
    } else {
      predictedTemp -= 5 + Math.random() * 5; // Cooling down during off-hours
    }

    // Vibration prediction with trend
    let predictedVib = lastData.vibration + vibTrend * i * 0.5;

    if (isWorkingHours) {
      predictedVib += Math.sin(i * 0.3) * 0.5 + Math.random() * 0.4 - 0.2;
    } else {
      predictedVib = 0.1 + Math.random() * 0.2; // Minimal vibration during off-hours
    }

    // Ensure realistic bounds
    predictedTemp = Math.max(20, Math.min(90, predictedTemp));
    predictedVib = Math.max(0, Math.min(8, predictedVib));

    // Calculate failure probability using multiple factors
    let failureProbability = baseRisk * equipmentRiskMultiplier;

    // Temperature factor
    if (predictedTemp > 70) {
      failureProbability += (predictedTemp - 70) * 2;
    }

    // Vibration factor
    if (predictedVib > 4.0) {
      failureProbability += (predictedVib - 4.0) * 8;
    }

    // Time-based degradation (slight increase over time)
    failureProbability += i * 0.5;

    // Trend-based adjustments
    if (tempTrend > 0.5) {
      failureProbability += tempTrend * 10;
    }
    if (vibTrend > 0.1) {
      failureProbability += vibTrend * 20;
    }

    // Add some randomness and ensure bounds
    failureProbability += Math.random() * 10 - 5;
    failureProbability = Math.max(0, Math.min(95, failureProbability));

    // Efficiency prediction (inverse relationship with failure probability)
    const predictedEfficiency = isWorkingHours
      ? Math.max(40, 95 - failureProbability * 0.3 + Math.random() * 10 - 5)
      : 0;

    // Spindle speed prediction
    const predictedSpindle = isWorkingHours
      ? Math.max(10000, lastData.spindle + Math.random() * 2000 - 1000)
      : 0;

    predictions.push({
      time: hour,
      timestamp: futureTime.getTime(),
      predictedTemperature: Math.round(predictedTemp * 100) / 100,
      predictedVibration: Math.round(predictedVib * 100) / 100,
      predictedEfficiency: Math.round(predictedEfficiency * 100) / 100,
      predictedSpindle: Math.round(predictedSpindle),
      failureProbability: Math.round(failureProbability * 100) / 100,
      confidenceLevel: Math.max(70, 95 - i * 2), // Confidence decreases with time
      riskFactors: {
        temperature: (predictedTemp > 70
          ? "high"
          : predictedTemp > 60
            ? "medium"
            : "low") as "high" | "medium" | "low",
        vibration: (predictedVib > 4.0
          ? "high"
          : predictedVib > 2.5
            ? "medium"
            : "low") as "high" | "medium" | "low",
        trend:
          tempTrend > 0.5 || vibTrend > 0.1
            ? ("deteriorating" as const)
            : ("stable" as const),
        overall: (failureProbability > 60
          ? "critical"
          : failureProbability > 30
            ? "warning"
            : "normal") as
          | "critical"
          | "high"
          | "medium"
          | "low"
          | "normal"
          | "warning",
      },
    });
  }

  return predictions;
};

// 주파수 스펙트럼 데이터 생성 함수
const generateFrequencySpectrumData = (equipment: Equipment) => {
  const frequencies = [];

  // 베어링 결함 주파수 계산 (예시)
  const shaftSpeed = equipment.spindle ? equipment.spindle / 60 : 0; // Hz
  const bearingFreqs = {
    BPFO: shaftSpeed * 3.5, // Ball Pass Frequency Outer race
    BPFI: shaftSpeed * 5.4, // Ball Pass Frequency Inner race
    BSF: shaftSpeed * 2.3, // Ball Spin Frequency
    FTF: shaftSpeed * 0.4, // Fundamental Train Frequency
  };

  // 주파수 범위: 0 ~ 2000 Hz, 더 조밀한 샘플링
  for (let freq = 0; freq <= 2000; freq += 5) {
    let amplitude = 0;

    if (equipment.status === "offline") {
      amplitude = 0;
    } else {
      // 백그라운드 노이즈 (더 현실적으로)
      amplitude = Math.random() * 0.05 + 0.01;

      // 회전 주파수 (1X) - 가장 높은 피크
      if (Math.abs(freq - shaftSpeed) < 3) {
        const proximity = 1 - Math.abs(freq - shaftSpeed) / 3;
        amplitude += (4.0 + Math.random() * 1.0) * proximity;
      }

      // 2X 하모닉
      if (Math.abs(freq - shaftSpeed * 2) < 3) {
        const proximity = 1 - Math.abs(freq - shaftSpeed * 2) / 3;
        amplitude += (1.5 + Math.random() * 0.5) * proximity;
      }

      // 3X 하모닉
      if (Math.abs(freq - shaftSpeed * 3) < 3) {
        const proximity = 1 - Math.abs(freq - shaftSpeed * 3) / 3;
        amplitude += (0.8 + Math.random() * 0.3) * proximity;
      }

      // 베어링 결함 주파수들 (더 현실적인 대역폭)
      Object.values(bearingFreqs).forEach((bearingFreq) => {
        if (Math.abs(freq - bearingFreq) < 5) {
          const proximity = 1 - Math.abs(freq - bearingFreq) / 5;
          let defectSeverity =
            equipment.status === "warning"
              ? 0.8 + Math.random() * 0.7
              : 0.1 + Math.random() * 0.3;
          amplitude += defectSeverity * proximity;
        }
      });

      // 기어 메시 주파수 (예시) - 더 현실적인 대역폭
      const gearMeshFreq = shaftSpeed * 24; // 24 teeth gear
      if (Math.abs(freq - gearMeshFreq) < 8) {
        const proximity = 1 - Math.abs(freq - gearMeshFreq) / 8;
        amplitude += (2.2 + Math.random() * 0.8) * proximity;
      }

      // 사이드밴드 (기어 메시 주파수 주변)
      const sidebandSpacing = shaftSpeed;
      for (let i = 1; i <= 3; i++) {
        if (Math.abs(freq - (gearMeshFreq + i * sidebandSpacing)) < 3) {
          const proximity =
            1 - Math.abs(freq - (gearMeshFreq + i * sidebandSpacing)) / 3;
          amplitude += ((0.5 + Math.random() * 0.3) * proximity) / i;
        }
        if (Math.abs(freq - (gearMeshFreq - i * sidebandSpacing)) < 3) {
          const proximity =
            1 - Math.abs(freq - (gearMeshFreq - i * sidebandSpacing)) / 3;
          amplitude += ((0.5 + Math.random() * 0.3) * proximity) / i;
        }
      }

      // 고주파 롤오프 (현실적인 감소)
      if (freq > 1000) {
        amplitude *= Math.exp(-(freq - 1000) / 500);
      }
    }

    frequencies.push({
      frequency: freq,
      amplitude: Math.round(amplitude * 1000) / 1000, // 더 정밀한 값
      phase: Math.round(Math.random() * 360),
    });
  }

  return { spectrumData: frequencies, resonantFreqs: bearingFreqs, shaftSpeed };
};

// 3축 진동 차트 컴포넌트
const VibrationChart3Axis = ({
  data,
  isAnimationActive = false,
}: {
  data: Vibration3AxisDataPoint[];
  isAnimationActive?: boolean;
}) => (
  <Box>
    <VStack gap={4} align="stretch">
      <Box textAlign="center">
        <Text fontSize="lg" fontWeight="bold" color="gray.700">
          3축 진동 분석 (24시간)
        </Text>
        <Text fontSize="sm" color="gray.500">
          X축(수평), Y축(수직), Z축(축방향) 진동 모니터링
        </Text>
      </Box>

      <Box height="400px" p={4} bg="gray.50" borderRadius="md">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />

            <XAxis
              dataKey="time"
              tick={{ fontSize: 12, fill: "#4a5568" }}
              tickFormatter={(value) => value.slice(0, 5)}
            />

            <YAxis
              tick={{ fontSize: 12, fill: "#4a5568" }}
              tickFormatter={(value) => `${value} mm/s`}
              label={{
                value: "진동 속도 (mm/s)",
                angle: -90,
                position: "insideLeft",
              }}
            />

            <Tooltip
              formatter={(value, name) => [
                `${Number(value).toFixed(2)} mm/s`,
                name,
              ]}
              labelFormatter={(label) => `시간: ${label}`}
            />

            {/* 임계값 라인들 */}
            <ReferenceLine
              y={4.5}
              stroke="#f59e0b"
              strokeDasharray="5 5"
              label="주의 수준"
            />
            <ReferenceLine
              y={7.1}
              stroke="#e53e3e"
              strokeDasharray="5 5"
              label="위험 수준"
            />

            <Line
              type="monotone"
              dataKey="vibrationX"
              stroke="#e53e3e"
              strokeWidth={2}
              name="X축 (수평)"
              isAnimationActive={isAnimationActive}
            />
            <Line
              type="monotone"
              dataKey="vibrationY"
              stroke="#3182ce"
              strokeWidth={2}
              name="Y축 (수직)"
            />
            <Line
              type="monotone"
              dataKey="vibrationZ"
              stroke="#38a169"
              strokeWidth={2}
              name="Z축 (축방향)"
            />
            <Line
              type="monotone"
              dataKey="overall"
              stroke="#805ad5"
              strokeWidth={3}
              name="Overall RMS"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* 3축 진동 통계 */}
      <Grid templateColumns="repeat(4, 1fr)" gap={4}>
        <Box textAlign="center" p={3} bg="red.50" borderRadius="md">
          <Text fontSize="xs" color="gray.600">
            X축 (수평)
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="red.600">
            {data[data.length - 1]?.vibrationX?.toFixed(2)} mm/s
          </Text>
        </Box>
        <Box textAlign="center" p={3} bg="blue.50" borderRadius="md">
          <Text fontSize="xs" color="gray.600">
            Y축 (수직)
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="blue.600">
            {data[data.length - 1]?.vibrationY?.toFixed(2)} mm/s
          </Text>
        </Box>
        <Box textAlign="center" p={3} bg="green.50" borderRadius="md">
          <Text fontSize="xs" color="gray.600">
            Z축 (축방향)
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="green.600">
            {data[data.length - 1]?.vibrationZ?.toFixed(2)} mm/s
          </Text>
        </Box>
        <Box textAlign="center" p={3} bg="purple.50" borderRadius="md">
          <Text fontSize="xs" color="gray.600">
            Overall RMS
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="purple.600">
            {data[data.length - 1]?.overall?.toFixed(2)} mm/s
          </Text>
        </Box>
      </Grid>

      {/* 진동 분석 인사이트 */}
      <CustomCard bg="blue.50" borderColor="blue.200">
        <VStack gap={3} align="stretch">
          <HStack>
            <Text fontSize="lg">📊</Text>
            <Text fontSize="md" fontWeight="bold" color="blue.700">
              진동 분석 결과
            </Text>
          </HStack>

          <VStack gap={2} align="stretch">
            {(() => {
              const latest = data[data.length - 1];
              const insights = [];

              if (latest?.vibrationX > 4.5) {
                insights.push(
                  "🔴 X축 진동 높음: 불균형 또는 미스얼라인먼트 의심"
                );
              }
              if (latest?.vibrationY > 4.5) {
                insights.push("🔴 Y축 진동 높음: 베어링 마모 또는 공진 가능성");
              }
              if (latest?.vibrationZ > 4.5) {
                insights.push(
                  "🔴 Z축 진동 높음: 축 정렬 불량 또는 커플링 문제"
                );
              }
              if (latest?.overall > 7.0) {
                insights.push("⚠️ 전체 진동 수준 위험: 즉시 점검 필요");
              }

              if (insights.length === 0) {
                insights.push("✅ 모든 축의 진동 수준이 정상 범위입니다");
              }

              return insights.map((insight) => (
                <Text key={insight} fontSize="sm" color="blue.600">
                  • {insight}
                </Text>
              ));
            })()}
          </VStack>
        </VStack>
      </CustomCard>
    </VStack>
  </Box>
);
const TimeSeriesChart = ({
  data,
  selectedSensor,
  isAnimationActive = false,
}: {
  data: HistoricalDataPoint[];
  selectedSensor: string;
  isAnimationActive?: boolean;
}) => {
  const sensor = sensorTypes.find((s) => s.key === selectedSensor);

  // 현실적인 임계값 설정
  const getThresholdLines = (sensorKey: string) => {
    switch (sensorKey) {
      case "temperature":
        return [
          { value: 70, color: "#f59e0b", label: "주의 온도" },
          { value: 80, color: "#e53e3e", label: "위험 온도" },
        ];
      case "vibration":
        return [
          { value: 4.0, color: "#f59e0b", label: "주의 진동" },
          { value: 5.0, color: "#e53e3e", label: "위험 진동" },
        ];
      case "efficiency":
        return [
          { value: 80, color: "#38a169", label: "목표 효율" },
          { value: 90, color: "#059669", label: "우수 효율" },
        ];
      case "spindle":
        return [{ value: 20000, color: "#f59e0b", label: "최대 권장 RPM" }];
      case "load":
        return [
          { value: 80, color: "#f59e0b", label: "높은 부하" },
          { value: 90, color: "#e53e3e", label: "과부하 위험" },
        ];
      case "feed":
        return [{ value: 2000, color: "#f59e0b", label: "높은 이송속도" }];
      default:
        return [];
    }
  };

  const thresholds = getThresholdLines(selectedSensor);

  return (
    <Box>
      <VStack align="stretch" gap={4}>
        <Box textAlign="center">
          <Text fontSize="lg" fontWeight="bold" color="gray.700">
            {sensor?.description} 추이 (24시간)
          </Text>
          <Text fontSize="sm" color="gray.500">
            실시간 모니터링 데이터 (작업시간: 08:00-18:00)
          </Text>
        </Box>

        <Box height="400px" p={4} bg="gray.50" borderRadius="md">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <defs>
                <linearGradient
                  id={`gradient-${selectedSensor}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={sensor?.color}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={sensor?.color}
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />

              <XAxis
                dataKey="time"
                tick={{ fontSize: 12, fill: "#4a5568" }}
                tickFormatter={(value) => value.slice(0, 5)}
                axisLine={{ stroke: "#cbd5e0" }}
              />

              <YAxis
                tick={{ fontSize: 12, fill: "#4a5568" }}
                tickFormatter={(value) =>
                  `${Number(value).toFixed(2)}${sensor?.unit}`
                }
                axisLine={{ stroke: "#cbd5e0" }}
                domain={["dataMin - 5", "dataMax + 5"]}
              />

              <Tooltip
                formatter={(value) => [
                  `${Number(value).toFixed(2)}${sensor?.unit}`,
                  sensor?.description,
                ]}
                labelFormatter={(label) => `시간: ${label}`}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />

              {/* 임계값 라인들 */}
              {thresholds.map((threshold) => (
                <Line
                  key={`threshold-${threshold.value}-${threshold.color}`}
                  type="monotone"
                  dataKey={() => threshold.value}
                  stroke={threshold.color}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                />
              ))}

              <Area
                type="monotone"
                dataKey={selectedSensor}
                stroke={sensor?.color}
                strokeWidth={3}
                isAnimationActive={isAnimationActive}
                fill={`url(#gradient-${selectedSensor})`}
                dot={{
                  fill: sensor?.color,
                  strokeWidth: 2,
                  r: 4,
                  stroke: "#fff",
                }}
                activeDot={{
                  r: 6,
                  stroke: sensor?.color,
                  strokeWidth: 3,
                  fill: "#fff",
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        {/* 통계 정보 */}
        <Grid templateColumns="repeat(4, 1fr)" gap={4}>
          <Box textAlign="center" p={3} bg="blue.50" borderRadius="md">
            <Text fontSize="xs" color="gray.600">
              현재값
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="blue.600">
              {data[data.length - 1]
                ? (
                    data[data.length - 1][
                      selectedSensor as keyof HistoricalDataPoint
                    ] as number
                  )?.toFixed(2)
                : "N/A"}
              {sensor?.unit}
            </Text>
          </Box>
          <Box textAlign="center" p={3} bg="green.50" borderRadius="md">
            <Text fontSize="xs" color="gray.600">
              최대값
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="green.600">
              {Math.max(
                ...data.map(
                  (d) =>
                    d[selectedSensor as keyof HistoricalDataPoint] as number
                )
              ).toFixed(2)}
              {sensor?.unit}
            </Text>
          </Box>
          <Box textAlign="center" p={3} bg="orange.50" borderRadius="md">
            <Text fontSize="xs" color="gray.600">
              최소값
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="orange.600">
              {Math.min(
                ...data.map(
                  (d) =>
                    d[selectedSensor as keyof HistoricalDataPoint] as number
                )
              ).toFixed(2)}
              {sensor?.unit}
            </Text>
          </Box>
          <Box textAlign="center" p={3} bg="purple.50" borderRadius="md">
            <Text fontSize="xs" color="gray.600">
              평균값
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="purple.600">
              {(
                data.reduce(
                  (sum, d) =>
                    sum +
                    (d[selectedSensor as keyof HistoricalDataPoint] as number),
                  0
                ) / data.length
              ).toFixed(2)}
              {sensor?.unit}
            </Text>
          </Box>
        </Grid>

        {/* 임계값 정보 */}
        {thresholds.length > 0 && (
          <Box
            p={3}
            bg="yellow.50"
            borderRadius="md"
            borderLeft="4px"
            borderLeftColor="yellow.400"
          >
            <Text fontSize="sm" fontWeight="medium" color="yellow.800">
              임계값 정보:
            </Text>
            {thresholds.map((threshold) => (
              <Text
                key={`threshold-${threshold.value}-${threshold.label}`}
                fontSize="xs"
                color="yellow.700"
              >
                • {threshold.label}: {threshold.value}
                {sensor?.unit}
              </Text>
            ))}
          </Box>
        )}
      </VStack>
    </Box>
  );
};

// 주파수 스펙트럼 차트 컴포넌트
// 주파수 스펙트럼 차트 컴포넌트 (수정됨)
// 주파수 스펙트럼 차트 컴포넌트 (한글 명칭 포함)
const FrequencySpectrumChart = ({
  spectrumData,
  resonantFreqs,
  shaftSpeed,
  equipment,
}: {
  spectrumData: SpectrumDataPoint[];
  resonantFreqs: ResonantFreqs;
  shaftSpeed: number;
  equipment: Equipment;
}) => {
  // 주요 주파수들의 실제 앰플리튜드 계산
  const getAmplitudeAtFrequency = (
    targetFreq: number,
    bandwidth: number = 3
  ) => {
    const nearbyPoints = spectrumData.filter(
      (d) => Math.abs(d.frequency - targetFreq) <= bandwidth
    );
    return nearbyPoints.length > 0
      ? Math.max(...nearbyPoints.map((d) => d.amplitude))
      : 0;
  };

  // 주요 주파수별 데이터 생성 (한글 명칭 포함)
  const majorFrequencies = [
    {
      frequency: shaftSpeed,
      amplitude: getAmplitudeAtFrequency(shaftSpeed),
      label: "1X",
      labelKor: "기본회전주파수",
      description: "축 회전 기본 주파수",
      color: "#e53e3e",
      type: "rotation",
    },
    {
      frequency: shaftSpeed * 2,
      amplitude: getAmplitudeAtFrequency(shaftSpeed * 2),
      label: "2X",
      labelKor: "2차 고조파",
      description: "미스얼라인먼트 지표",
      color: "#dd6b20",
      type: "rotation",
    },
    {
      frequency: shaftSpeed * 3,
      amplitude: getAmplitudeAtFrequency(shaftSpeed * 3),
      label: "3X",
      labelKor: "3차 고조파",
      description: "각도 미스얼라인먼트",
      color: "#f59e0b",
      type: "rotation",
    },
    {
      frequency: shaftSpeed * 24,
      amplitude: getAmplitudeAtFrequency(shaftSpeed * 24, 8),
      label: "GMF",
      labelKor: "기어 맞물림주파수",
      description: "기어 메시 주파수",
      color: "#38a169",
      type: "gear",
    },
    {
      frequency: resonantFreqs.BPFO,
      amplitude: getAmplitudeAtFrequency(resonantFreqs.BPFO, 5),
      label: "BPFO",
      labelKor: "외륜 통과주파수",
      description: "베어링 외륜 볼 통과 주파수",
      color: "#805ad5",
      type: "bearing",
    },
    {
      frequency: resonantFreqs.BPFI,
      amplitude: getAmplitudeAtFrequency(resonantFreqs.BPFI, 5),
      label: "BPFI",
      labelKor: "내륜 통과주파수",
      description: "베어링 내륜 볼 통과 주파수",
      color: "#805ad5",
      type: "bearing",
    },
    {
      frequency: resonantFreqs.BSF,
      amplitude: getAmplitudeAtFrequency(resonantFreqs.BSF, 5),
      label: "BSF",
      labelKor: "볼 회전주파수",
      description: "베어링 볼 스핀 주파수",
      color: "#9f7aea",
      type: "bearing",
    },
    {
      frequency: resonantFreqs.FTF,
      amplitude: getAmplitudeAtFrequency(resonantFreqs.FTF, 5),
      label: "FTF",
      labelKor: "보정기 회전주파수",
      description: "베어링 케이지 회전 주파수",
      color: "#b794f6",
      type: "bearing",
    },
  ].filter((freq) => freq.frequency > 0 && freq.frequency <= 2000);

  return (
    <Box>
      <VStack gap={4} align="stretch">
        <Box textAlign="center">
          <Text fontSize="lg" fontWeight="bold" color="gray.700">
            진동 주파수 스펙트럼 분석
          </Text>
          <Text fontSize="sm" color="gray.500">
            주파수 도메인 분석 및 고유주파수 표시 (현재 상태)
          </Text>
        </Box>

        <Box
          height="450px"
          p={4}
          bg="gray.50"
          borderRadius="md"
          position="relative"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={spectrumData}
              margin={{ top: 40, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />

              <XAxis
                dataKey="frequency"
                tick={{ fontSize: 12, fill: "#4a5568" }}
                label={{
                  value: "주파수 (Hz)",
                  position: "insideBottom",
                  offset: -10,
                }}
                domain={[0, 2000]}
              />

              <YAxis
                tick={{ fontSize: 12, fill: "#4a5568" }}
                label={{
                  value: "진폭 (mm/s)",
                  angle: -90,
                  position: "insideLeft",
                }}
                domain={[0, "dataMax + 0.5"]}
              />

              <Tooltip
                formatter={(value, _name) => [
                  `${Number(value).toFixed(3)} mm/s`,
                  "진폭",
                ]}
                labelFormatter={(label) => `주파수: ${label} Hz`}
              />

              {/* 메인 스펙트럼 라인 */}
              <Line
                type="linear"
                dataKey="amplitude"
                stroke="#3182ce"
                strokeWidth={1.5}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
                animationDuration={400}
                animationEasing="ease-in-out"
              />
            </LineChart>
          </ResponsiveContainer>

          {/* 주요 주파수 Bar들을 오버레이로 표시 */}
          <Box
            position="absolute"
            top="40px"
            left="20px"
            right="30px"
            bottom="40px"
            pointerEvents="none"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={majorFrequencies}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="frequency"
                  axisLine={false}
                  tickLine={false}
                  tick={false}
                  domain={[0, 2000]}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={false}
                  domain={[0, "dataMax + 0.5"]}
                />

                <Tooltip
                  formatter={(value, _name, props) => [
                    `${Number(value).toFixed(3)} mm/s`,
                    `${props.payload.labelKor} (${props.payload.label})`,
                  ]}
                  labelFormatter={(_label, payload) =>
                    payload && payload[0]
                      ? `${payload[0].payload.description} - ${payload[0].payload.frequency.toFixed(1)} Hz`
                      : ""
                  }
                />

                {/* 회전 주파수 Bar들 */}
                {majorFrequencies
                  .filter((f) => f.type === "rotation")
                  .map((freq) => (
                    <Bar
                      key={`rotation-bar-${freq.frequency}-${freq.label}`}
                      dataKey="amplitude"
                      fill={freq.color}
                      maxBarSize={8}
                      opacity={0.8}
                    />
                  ))}

                {/* 기어 주파수 Bar */}
                {majorFrequencies
                  .filter((f) => f.type === "gear")
                  .map((freq) => (
                    <Bar
                      key={`gear-bar-${freq.frequency}-${freq.label}`}
                      dataKey="amplitude"
                      fill={freq.color}
                      maxBarSize={8}
                      opacity={0.8}
                    />
                  ))}

                {/* 베어링 주파수 Bar들 */}
                {majorFrequencies
                  .filter((f) => f.type === "bearing")
                  .map((freq) => (
                    <Bar
                      key={`bearing-bar-${freq.frequency}-${freq.label}`}
                      dataKey="amplitude"
                      fill={freq.color}
                      maxBarSize={6}
                      opacity={0.7}
                    />
                  ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* 주파수 라벨들을 상단에 표시 (한글 포함) */}
          <Box
            position="absolute"
            top="5px"
            left="20px"
            right="30px"
            height="30px"
          >
            {majorFrequencies.map((freq) => {
              const xPosition = `${(freq.frequency / 2000) * 100}%`;
              return (
                <Box
                  key={`major-freq-${freq.frequency}-${freq.label}`}
                  position="absolute"
                  left={xPosition}
                  transform="translateX(-50%)"
                  textAlign="center"
                >
                  <Text
                    fontSize="10px"
                    fontWeight="bold"
                    color={freq.color}
                    bg="white"
                    px={1}
                    borderRadius="2px"
                    border="1px solid"
                    borderColor={freq.color}
                    title={freq.description}
                  >
                    {freq.label}
                  </Text>
                  <Text fontSize="8px" color="gray.500">
                    {freq.frequency.toFixed(0)}Hz
                  </Text>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* 주파수 분석 정보 (한글 명칭 포함) */}
        <Grid templateColumns="repeat(3, 1fr)" gap={4}>
          <CustomCard>
            <VStack gap={3} align="stretch">
              <Heading size="sm" color="red.600">
                🔴 회전 주파수 (Rotational Frequencies)
              </Heading>
              <VStack gap={2} align="stretch">
                {majorFrequencies
                  .filter((f) => f.type === "rotation")
                  .map((freq) => (
                    <VStack
                      key={`rotation-${freq.frequency}-${freq.label}`}
                      gap={1}
                      align="stretch"
                    >
                      <HStack justify="space-between">
                        <VStack gap={0} align="start">
                          <Text fontSize="sm" fontWeight="bold">
                            {freq.label} - {freq.labelKor}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {freq.description}
                          </Text>
                        </VStack>
                        <VStack gap={0} align="end">
                          <Badge colorScheme="red">
                            {freq.frequency.toFixed(1)} Hz
                          </Badge>
                          <Text fontSize="xs" color="gray.500">
                            {freq.amplitude.toFixed(3)} mm/s
                          </Text>
                        </VStack>
                      </HStack>
                    </VStack>
                  ))}
              </VStack>
            </VStack>
          </CustomCard>

          <CustomCard>
            <VStack gap={3} align="stretch">
              <Heading size="sm" color="purple.600">
                🟣 베어링 결함 주파수 (Bearing Defect Frequencies)
              </Heading>
              <VStack gap={2} align="stretch">
                {majorFrequencies
                  .filter((f) => f.type === "bearing")
                  .map((freq) => (
                    <VStack
                      key={`bearing-${freq.frequency}-${freq.label}`}
                      gap={1}
                      align="stretch"
                    >
                      <HStack justify="space-between">
                        <VStack gap={0} align="start">
                          <Text fontSize="sm" fontWeight="bold">
                            {freq.label} - {freq.labelKor}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {freq.description}
                          </Text>
                        </VStack>
                        <VStack gap={0} align="end">
                          <Badge colorScheme="purple">
                            {freq.frequency.toFixed(1)} Hz
                          </Badge>
                          <Text fontSize="xs" color="gray.500">
                            {freq.amplitude.toFixed(3)} mm/s
                          </Text>
                        </VStack>
                      </HStack>
                    </VStack>
                  ))}
              </VStack>
            </VStack>
          </CustomCard>

          <CustomCard>
            <VStack gap={3} align="stretch">
              <Heading size="sm" color="green.600">
                ⚙️ 기어 주파수 (Gear Frequencies)
              </Heading>
              <VStack gap={2} align="stretch">
                {majorFrequencies
                  .filter((f) => f.type === "gear")
                  .map((freq) => (
                    <VStack
                      key={`gear-${freq.frequency}-${freq.label}`}
                      gap={1}
                      align="stretch"
                    >
                      <HStack justify="space-between">
                        <VStack gap={0} align="start">
                          <Text fontSize="sm" fontWeight="bold">
                            {freq.label} - {freq.labelKor}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {freq.description}
                          </Text>
                        </VStack>
                        <VStack gap={0} align="end">
                          <Badge colorScheme="green">
                            {freq.frequency.toFixed(1)} Hz
                          </Badge>
                          <Text fontSize="xs" color="gray.500">
                            {freq.amplitude.toFixed(3)} mm/s
                          </Text>
                        </VStack>
                      </HStack>
                    </VStack>
                  ))}
              </VStack>
            </VStack>
          </CustomCard>
        </Grid>

        {/* 용어 설명 */}
        <CustomCard bg="blue.50" borderColor="blue.200">
          <VStack gap={3} align="stretch">
            <Heading size="sm" color="blue.700">
              📚 주파수 용어 설명
            </Heading>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <VStack gap={2} align="stretch">
                <Text fontSize="sm" fontWeight="bold" color="blue.600">
                  회전 주파수 (Rotational)
                </Text>
                <Text fontSize="xs" color="blue.600">
                  • 1X: 축의 기본 회전 주파수, 불균형 지표
                </Text>
                <Text fontSize="xs" color="blue.600">
                  • 2X: 미스얼라인먼트의 주요 지표
                </Text>
                <Text fontSize="xs" color="blue.600">
                  • 3X: 각도 미스얼라인먼트 특성
                </Text>
              </VStack>
              <VStack gap={2} align="stretch">
                <Text fontSize="sm" fontWeight="bold" color="blue.600">
                  베어링 결함 주파수 (Bearing)
                </Text>
                <Text fontSize="xs" color="blue.600">
                  • BPFO: 외륜 결함시 나타나는 주파수
                </Text>
                <Text fontSize="xs" color="blue.600">
                  • BPFI: 내륜 결함시 나타나는 주파수
                </Text>
                <Text fontSize="xs" color="blue.600">
                  • BSF: 볼 베어링 결함시 주파수
                </Text>
                <Text fontSize="xs" color="blue.600">
                  • FTF: 케이지 결함시 나타나는 주파수
                </Text>
              </VStack>
            </Grid>
          </VStack>
        </CustomCard>

        {/* 범례 (한글 포함) */}
        <CustomCard bg="gray.50" borderColor="gray.300">
          <VStack gap={2} align="stretch">
            <Heading size="sm" color="gray.700">
              📊 스펙트럼 범례
            </Heading>
            <HStack gap={6} flexWrap="wrap">
              <HStack>
                <Box w={4} h={0.5} bg="#3182ce" />
                <Text fontSize="sm">진동 스펙트럼 (Vibration Spectrum)</Text>
              </HStack>
              <HStack>
                <Box w={2} h={4} bg="#e53e3e" />
                <Text fontSize="sm">회전 주파수 (Rotational Freq.)</Text>
              </HStack>
              <HStack>
                <Box w={2} h={4} bg="#38a169" />
                <Text fontSize="sm">기어 주파수 (Gear Freq.)</Text>
              </HStack>
              <HStack>
                <Box w={2} h={4} bg="#805ad5" />
                <Text fontSize="sm">베어링 주파수 (Bearing Freq.)</Text>
              </HStack>
            </HStack>
          </VStack>
        </CustomCard>

        {/* 주파수 분석 결과 (한글 용어 포함) */}
        <CustomCard bg="yellow.50" borderColor="yellow.200">
          <VStack gap={3} align="stretch">
            <HStack>
              <Text fontSize="lg">🔬</Text>
              <Text fontSize="md" fontWeight="bold" color="yellow.700">
                주파수 도메인 분석 결과
              </Text>
            </HStack>

            <VStack gap={2} align="stretch">
              {(() => {
                const insights = [];

                // 각 주요 주파수의 임계값 체크
                const rotationFreqs = majorFrequencies.filter(
                  (f) => f.type === "rotation"
                );
                const bearingFreqs = majorFrequencies.filter(
                  (f) => f.type === "bearing"
                );
                const gearFreqs = majorFrequencies.filter(
                  (f) => f.type === "gear"
                );

                // 회전 주파수 분석
                const peak1X =
                  rotationFreqs.find((f) => f.label === "1X")?.amplitude || 0;
                const peak2X =
                  rotationFreqs.find((f) => f.label === "2X")?.amplitude || 0;

                if (peak1X > 3.0) {
                  insights.push(
                    `🔴 기본회전주파수(1X) 높음 (${peak1X.toFixed(3)} mm/s): 축 불균형 또는 편심`
                  );
                }
                if (peak2X > 1.2) {
                  insights.push(
                    `🟡 2차고조파(2X) 높음 (${peak2X.toFixed(3)} mm/s): 축 미스얼라인먼트`
                  );
                }
                if (peak2X > peak1X * 0.5) {
                  insights.push(`⚠️ 2X/1X 비율 높음: 각도 미스얼라인먼트 의심`);
                }

                // 베어링 주파수 분석
                const maxBearingAmp = Math.max(
                  ...bearingFreqs.map((f) => f.amplitude)
                );
                if (maxBearingAmp > 0.5) {
                  const dominantBearing = bearingFreqs.find(
                    (f) => f.amplitude === maxBearingAmp
                  );
                  if (dominantBearing) {
                    insights.push(
                      `🔴 ${dominantBearing.labelKor}(${dominantBearing.label}) 높음 (${maxBearingAmp.toFixed(3)} mm/s): 베어링 결함`
                    );
                  }
                }

                // 기어 주파수 분석
                const gearAmp = gearFreqs[0]?.amplitude || 0;
                if (gearAmp > 1.8) {
                  insights.push(
                    `🟡 기어맞물림주파수(GMF) 높음 (${gearAmp.toFixed(3)} mm/s): 기어 마모 의심`
                  );
                }

                // 전체 상태 평가
                if (equipment.status === "warning") {
                  insights.push("⚠️ 장비 경고 상태: 정밀 진동 진단 권장");
                }

                if (insights.length === 0) {
                  insights.push("✅ 모든 주파수 성분이 정상 범위에 있습니다");
                  insights.push(
                    `📊 주요 진폭 수준 - 1X: ${peak1X.toFixed(3)}, 베어링 최대: ${maxBearingAmp.toFixed(3)} mm/s`
                  );
                }

                return insights.map((insight) => (
                  <Text key={insight} fontSize="sm" color="yellow.700">
                    • {insight}
                  </Text>
                ));
              })()}
            </VStack>
          </VStack>
        </CustomCard>
      </VStack>
    </Box>
  );
};

const AIPredictionChart = ({
  historicalData,
  predictions,
  equipment,
  isAnimationActive = false,
}: {
  historicalData: HistoricalDataPoint[];
  predictions: AIPredictionPoint[];
  equipment: Equipment;
  isAnimationActive?: boolean;
}) => {
  // 과거 + 예측 데이터 결합
  const combinedData = [
    ...historicalData.slice(-12).map((d) => ({ ...d, type: "historical" })),
    ...predictions.slice(0, 12).map((d) => ({ ...d, type: "prediction" })),
  ];

  return (
    <Box>
      <VStack gap={6} align="stretch">
        <Box textAlign="center">
          <Text fontSize="lg" fontWeight="bold" color="gray.700">
            🤖 AI 예지보전 분석
          </Text>
          <Text fontSize="sm" color="gray.500">
            과거 12시간 + 향후 12시간 예측
          </Text>
        </Box>

        {/* 예측 차트 */}
        <Box height="400px" p={4} bg="gray.50" borderRadius="md">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={combinedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />

              <XAxis
                dataKey="time"
                tick={{ fontSize: 12, fill: "#4a5568" }}
                tickFormatter={(value) => value.slice(0, 5)}
              />

              <YAxis
                yAxisId="temp"
                tick={{ fontSize: 12, fill: "#4a5568" }}
                tickFormatter={(value) => `${value}°C`}
              />

              <YAxis
                yAxisId="prob"
                orientation="right"
                tick={{ fontSize: 12, fill: "#4a5568" }}
                tickFormatter={(value) => `${value}%`}
              />

              <Tooltip
                formatter={(value, name) => {
                  const nameStr =
                    typeof name === "string" ? name : String(name);
                  const unit = nameStr.includes("확률")
                    ? "%"
                    : nameStr.includes("온도")
                      ? "°C"
                      : "mm/s";
                  return [`${Number(value).toFixed(2)}${unit}`, name];
                }}
                labelFormatter={(label) => `시간: ${label}`}
              />

              {/* 과거 온도 */}
              <Line
                yAxisId="temp"
                type="monotone"
                dataKey="temperature"
                stroke="#3182ce"
                strokeWidth={2}
                dot={{ fill: "#3182ce", r: 3 }}
                connectNulls={false}
                isAnimationActive={isAnimationActive}
              />

              {/* 예측 온도 */}
              <Line
                yAxisId="temp"
                type="monotone"
                dataKey="predictedTemperature"
                stroke="#3182ce"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: "#3182ce", r: 3 }}
                connectNulls={false}
                isAnimationActive={isAnimationActive}
              />

              {/* 고장 확률 */}
              <Line
                yAxisId="prob"
                type="monotone"
                dataKey="failureProbability"
                stroke="#e53e3e"
                strokeWidth={3}
                dot={{ fill: "#e53e3e", r: 4 }}
                isAnimationActive={isAnimationActive}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* AI 인사이트 */}
        <Grid templateColumns="repeat(3, 1fr)" gap={6}>
          <CustomCard bg="red.50" borderColor="red.200">
            <VStack gap={2}>
              <Text fontSize="2xl">⚠️</Text>
              <Text fontSize="sm" fontWeight="bold" color="red.700">
                고장 위험도
              </Text>
              <Text fontSize="2xl" fontWeight="bold" color="red.600">
                {predictions[0]?.failureProbability?.toFixed(1)}%
              </Text>
              <Text fontSize="xs" color="red.600">
                향후 24시간 내
              </Text>
            </VStack>
          </CustomCard>

          <CustomCard bg="blue.50" borderColor="blue.200">
            <VStack gap={2}>
              <Text fontSize="2xl">🔧</Text>
              <Text fontSize="sm" fontWeight="bold" color="blue.700">
                예상 정비일
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="blue.600">
                {(() => {
                  const riskLevel = predictions[0]?.failureProbability || 0;
                  if (riskLevel > 70) return "즉시";
                  if (riskLevel > 50) return "3일 이내";
                  if (riskLevel > 30) return "1주일 이내";
                  return "정상";
                })()}
              </Text>
              <Text fontSize="xs" color="blue.600">
                AI 권장
              </Text>
            </VStack>
          </CustomCard>

          <CustomCard bg="green.50" borderColor="green.200">
            <VStack gap={2}>
              <Text fontSize="2xl">💡</Text>
              <Text fontSize="sm" fontWeight="bold" color="green.700">
                예상 절약
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="green.600">
                ₩514만
              </Text>
              <Text fontSize="xs" color="green.600">
                예지보전 효과
              </Text>
            </VStack>
          </CustomCard>
        </Grid>

        {/* AI 권장사항 */}
        <CustomCard bg="purple.50" borderColor="purple.200">
          <VStack gap={3} align="stretch">
            <HStack>
              <Text fontSize="lg"></Text>
              <Text fontSize="md" fontWeight="bold" color="purple.700">
                AI 권장사항
              </Text>
            </HStack>

            {(() => {
              const temp = predictions[0]?.predictedTemperature || 0;
              const vib = predictions[0]?.predictedVibration || 0;
              const risk = predictions[0]?.failureProbability || 0;

              const recommendations = [];

              if (temp > 75) {
                recommendations.push("🌡️ 냉각 시스템 점검 및 청소 권장");
              }
              if (vib > 4.0) {
                recommendations.push("📳 베어링 및 커플링 정렬 상태 확인 필요");
              }
              if (risk > 60) {
                recommendations.push("⚠️ 긴급 점검 스케줄링 권장");
              }
              if (equipment.status === "warning") {
                recommendations.push("🔧 전문 기술자 진단 권장");
              }

              if (recommendations.length === 0) {
                recommendations.push("✅ 현재 상태 양호, 정기 점검 유지");
              }

              return recommendations.map((rec) => (
                <Text key={rec} fontSize="sm" color="purple.600">
                  • {rec}
                </Text>
              ));
            })()}
          </VStack>
        </CustomCard>
      </VStack>
    </Box>
  );
};

// 기존 차트 컴포넌트들과 메인 컴포넌트는 동일하게 유지하되,
// selectedSensor가 'vibration_3axis' 또는 'vibration_frequency'일 때 새로운 차트를 렌더링

const EquipmentDetailDialog = ({
  isOpen,
  onClose,
  equipment,
}: {
  isOpen: boolean;
  onClose: () => void;
  equipment: Equipment | null;
}) => {
  const [selectedSensor, setSelectedSensor] = useState<string>("temperature");
  const [timeSeriesData, setTimeSeriesData] = useState<HistoricalDataPoint[]>(
    []
  );
  const [vibration3AxisData, setVibration3AxisData] = useState<
    Vibration3AxisDataPoint[]
  >([]);
  const [frequencyData, setFrequencyData] = useState<{
    spectrumData: SpectrumDataPoint[];
    resonantFreqs: ResonantFreqs;
    shaftSpeed: number;
  }>({
    spectrumData: [],
    resonantFreqs: { BPFO: 0, BPFI: 0, BSF: 0, FTF: 0 },
    shaftSpeed: 0,
  });
  const [predictions, setPredictions] = useState<AIPredictionPoint[]>([]);

  const [hasAnimated, setHasAnimated] = useState(false);

  // 데이터 생성 및 업데이트 로직은 기존과 동일하되, 진동 데이터 추가
  useEffect(() => {
    if (!equipment) return;

    // 기존 시계열 데이터 생성
    const initialData = generateInitialData(equipment); // Pass equipment here
    setTimeSeriesData(initialData);

    // 3축 진동 데이터 생성
    const vibrationData = generate3AxisVibrationData(equipment);
    setVibration3AxisData(vibrationData);

    // 주파수 스펙트럼 데이터 생성
    const freqData = generateFrequencySpectrumData(equipment);
    setFrequencyData(freqData);

    // AI 예측 생성
    if (initialData.length > 0) {
      const newPredictions = generateAIPredictions(equipment, initialData);
      setPredictions(newPredictions);
    }

    if (!hasAnimated) {
      setHasAnimated(true);
    }
  }, [equipment, hasAnimated]);

  // equipment가 없으면 null 반환
  if (!equipment) {
    return null;
  }

  // 센서 필터링
  const availableSensors =
    equipment.status === "offline"
      ? sensorTypes.filter((sensor) => ["temperature"].includes(sensor.key))
      : sensorTypes;

  // 차트 렌더링 함수
  const renderChart = () => {
    switch (selectedSensor) {
      case "vibration_3axis":
        return <VibrationChart3Axis data={vibration3AxisData} />;
      case "vibration_frequency":
        return (
          <FrequencySpectrumChart
            spectrumData={frequencyData.spectrumData}
            resonantFreqs={frequencyData.resonantFreqs}
            shaftSpeed={frequencyData.shaftSpeed}
            equipment={equipment}
          />
        );
      case "ai_prediction":
        return (
          <AIPredictionChart
            historicalData={timeSeriesData}
            predictions={predictions}
            equipment={equipment}
          />
        );
      default:
        return (
          <TimeSeriesChart
            data={timeSeriesData}
            selectedSensor={selectedSensor}
          />
        );
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxW="6xl" maxH="90vh" overflowY="auto">
          <Dialog.CloseTrigger />
          <Dialog.Header>
            <Dialog.Title>
              {equipment.name} 고급 진동 분석 & AI 모니터링
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Body>
            <VStack gap={6} align="stretch">
              {/* 현재 상태 요약 - 기존과 동일 */}

              {/* 센서 선택 버튼 및 차트 */}
              <CustomCard>
                <VStack gap={4} align="stretch">
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      mb={3}
                      color="gray.600"
                    >
                      분석할 센서를 선택하세요:
                    </Text>
                    <ButtonGroup
                      size="sm"
                      variant="outline"
                      gap={2}
                      flexWrap="wrap"
                    >
                      {availableSensors.map((sensor) => (
                        <Button
                          key={sensor.key}
                          onClick={() => setSelectedSensor(sensor.key)}
                          colorScheme={
                            selectedSensor === sensor.key ? "blue" : "gray"
                          }
                          variant={
                            selectedSensor === sensor.key ? "solid" : "outline"
                          }
                          _hover={{
                            transform:
                              selectedSensor !== sensor.key
                                ? "translateY(-1px)"
                                : "none",
                          }}
                          mb={2}
                        >
                          <HStack gap={2}>
                            <Text>{sensor.label.split(" ")[0]}</Text>
                            <Text>
                              {sensor.label.split(" ").slice(1).join(" ")}
                            </Text>
                          </HStack>
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Box>

                  {/* 선택된 센서의 차트 렌더링 */}
                  {renderChart()}
                </VStack>
              </CustomCard>
            </VStack>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

export default EquipmentDetailDialog;
